import type { NodeCategory } from '@/domain/funnel/node-system';
import type { Highlight } from './contracts';
import type { PresentationGraphInput } from './graph-input';
import { getStageFocus } from './stage-focus';

export type EmphasisState = 'full' | 'dimmed';

export interface GraphEmphasis {
  nodes: Map<string, EmphasisState>;
  edges: Map<string, EmphasisState>;
}

function allFull(graph: PresentationGraphInput): GraphEmphasis {
  return {
    nodes: new Map(graph.nodes.map((node) => [node.id, 'full' as const])),
    edges: new Map(graph.edges.map((edge) => [edge.id, 'full' as const])),
  };
}

/**
 * SPEC-011 §49/§50/§55/plan §24/§26/§27 — a single pure function covering
 * both "highlight focus" and "stage focus": exactly one is active at a
 * time (highlight takes precedence when both are somehow set), and
 * Overview (neither) means every element is 'full' (PRES-REQ-012: dimmed
 * elements stay visible enough for context — dimming is a rendering
 * concern the UI layer applies via opacity, this only classifies state).
 */
export function computeGraphEmphasis(
  graph: PresentationGraphInput,
  active: {
    highlight?: Pick<Highlight, 'nodeIds' | 'edgeIds'>;
    stageFocusCategory?: NodeCategory | string;
  },
): GraphEmphasis {
  if (active.highlight) {
    const focusedNodeIds = new Set(active.highlight.nodeIds);
    const focusedEdgeIds = new Set(active.highlight.edgeIds);
    return {
      nodes: new Map(
        graph.nodes.map((node) => [node.id, focusedNodeIds.has(node.id) ? 'full' : 'dimmed']),
      ),
      edges: new Map(
        graph.edges.map((edge) => [edge.id, focusedEdgeIds.has(edge.id) ? 'full' : 'dimmed']),
      ),
    };
  }
  if (active.stageFocusCategory) {
    const { focusedNodeIds, relevantEdgeIds } = getStageFocus(graph, active.stageFocusCategory);
    return {
      nodes: new Map(
        graph.nodes.map((node) => [node.id, focusedNodeIds.has(node.id) ? 'full' : 'dimmed']),
      ),
      edges: new Map(
        graph.edges.map((edge) => [edge.id, relevantEdgeIds.has(edge.id) ? 'full' : 'dimmed']),
      ),
    };
  }
  return allFull(graph);
}
